import React from 'react';
import KPICard from '../ui/KPICard';
import Card from '../ui/Card';
import Section from '../ui/Section';
import Table from '../ui/Table';
import Badge from '../ui/Badge';
import AlertsSection from '../ui/AlertsSection';
import { formatCurrency, formatNumber } from '../dashboardUtils';
import { useSalesOverviewQuery, useTopProductsQuery } from '../../../../api/sales.api';
import { useExpenseSummaryQuery } from '../../../../api/expense.api';
import { useFinanceTrendQuery } from '../../../../api/dashboard.api';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';

/**
 * Main Overview Page Component
 */
const OverviewDashboard = ({ dateRange }) => {
    const { from, to } = dateRange;

    // Queries reacting to global date filter
    const { data: salesData, isLoading: salesLoading } = useSalesOverviewQuery({ from, to });
    const { data: expenseData, isLoading: expenseLoading } = useExpenseSummaryQuery({ from, to });
    const { data: trendData, isLoading: trendLoading } = useFinanceTrendQuery({ from, to });
    const { data: topProductsData, isLoading: productsLoading } = useTopProductsQuery({
        sortBy: 'revenue',
        from,
        to,
    });

    const salesOverview = salesData?.result || {};
    const expenseSummary = expenseData?.result || {};
    const financeTrend = trendData?.result || trendData?.data || [];
    const topProductsList = (topProductsData?.result?.products || []).slice(0, 5);

    // KPI Metrics calculation
    const totalRevenue = salesOverview.totalRevenue || 0;
    const totalProfit = salesOverview.totalProfit || 0;
    const totalExpenses = expenseSummary.totalExpense || salesOverview.totalCost || 0;
    const cashIn = salesOverview.cashIn || totalRevenue - totalExpenses;

    // Table Column Config for Top 5 Products
    const productColumns = [
        {
            key: 'rank',
            label: 'Rank',
            align: 'center',
            render: (_, __, index) => {
                const rankColor =
                    index === 0
                        ? 'bg-amber-400 text-amber-950 font-bold'
                        : index === 1
                        ? 'bg-stone-300 text-stone-900 font-bold'
                        : index === 2
                        ? 'bg-amber-600/80 text-white font-bold'
                        : 'bg-stone-100 text-stone-600 font-medium';
                return (
                    <span
                        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs ${rankColor}`}
                    >
                        {index + 1}
                    </span>
                );
            },
        },
        {
            key: 'productName',
            label: 'Product',
            render: (val, row) => (
                <div>
                    <div className="font-semibold text-stone-900">{val || 'Unnamed Product'}</div>
                    <div className="text-[11px] text-stone-400 font-normal">
                        ID: {row.productId ? row.productId.slice(-8) : '-'}
                    </div>
                </div>
            ),
        },
        {
            key: 'unitsSold',
            label: 'Units Sold',
            align: 'right',
            render: (val) => formatNumber(val || 0),
        },
        {
            key: 'revenue',
            label: 'Revenue',
            align: 'right',
            render: (val) => (
                <span className="font-semibold text-stone-900">{formatCurrency(val)}</span>
            ),
        },
        {
            key: 'profit',
            label: 'Profit',
            align: 'right',
            render: (val) => (
                <span className="font-semibold text-emerald-600">{formatCurrency(val)}</span>
            ),
        },
        {
            key: 'profitMargin',
            label: 'Margin',
            align: 'right',
            render: (margin) => {
                const variant =
                    margin >= 40 ? 'success' : margin >= 20 ? 'warning' : 'danger';
                return <Badge variant={variant}>{margin || 0}%</Badge>;
            },
        },
    ];

    return (
        <div className="space-y-8 w-full min-w-0">
            {/* Section 1: KPI Cards */}
            <Section title="Key Metrics" subtitle="Overview of top-line revenue, profit and cash health">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full min-w-0">
                    <KPICard
                        label="Total Revenue"
                        value={formatCurrency(totalRevenue)}
                        trend="up"
                        sub="+12.4% vs last period"
                        accentClass="bg-stone-900 text-white"
                        loading={salesLoading}
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Net Profit"
                        value={formatCurrency(totalProfit)}
                        trend={totalProfit >= 0 ? 'up' : 'down'}
                        sub={totalProfit >= 0 ? 'Healthy Margin' : 'Action Required'}
                        accentClass="bg-emerald-600 text-white"
                        loading={salesLoading}
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Total Expenses"
                        value={formatCurrency(totalExpenses)}
                        trend="down"
                        sub="Managed Costs"
                        accentClass="bg-rose-500 text-white"
                        loading={expenseLoading}
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Cash In"
                        value={formatCurrency(cashIn)}
                        trend="up"
                        sub="Positive Flow"
                        accentClass="bg-indigo-600 text-white"
                        loading={salesLoading || expenseLoading}
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        }
                    />
                </div>
            </Section>

            {/* Section 2: Financial Trend Line Chart */}
            <Section title="Financial Trend" subtitle="Revenue, Profit & Expenses timeline analysis">
                <Card noPadding>
                    <div className="p-6 w-full min-w-0">
                        {trendLoading ? (
                            <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                Loading financial trend data...
                            </div>
                        ) : financeTrend.length === 0 ? (
                            <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                No trend data available for selected range
                            </div>
                        ) : (
                            <div className="h-80 w-full min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={financeTrend}
                                        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="date"
                                            stroke="#94a3b8"
                                            fontSize={11}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            stroke="#94a3b8"
                                            fontSize={11}
                                            tickLine={false}
                                            tickFormatter={(val) => `NPR ${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                borderColor: '#334155',
                                                borderRadius: '0.75rem',
                                                color: '#f8fafc',
                                                fontSize: '12px',
                                            }}
                                            formatter={(value) => [formatCurrency(value), '']}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            align="right"
                                            wrapperStyle={{ paddingBottom: '15px', fontSize: '12px' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="revenue"
                                            name="Revenue"
                                            stroke="#0f172a"
                                            strokeWidth={2.5}
                                            dot={{ r: 3, fill: '#0f172a' }}
                                            activeDot={{ r: 5 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="profit"
                                            name="Profit"
                                            stroke="#059669"
                                            strokeWidth={2.5}
                                            dot={{ r: 3, fill: '#059669' }}
                                            activeDot={{ r: 5 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="expense"
                                            name="Expenses"
                                            stroke="#e11d48"
                                            strokeWidth={2}
                                            strokeDasharray="4 4"
                                            dot={{ r: 3, fill: '#e11d48' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </Card>
            </Section>

            {/* Section 3 & Section 4 Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full min-w-0">
                {/* Top 5 Products Table */}
                <div className="lg:col-span-7 min-w-0">
                    <Section title="Top 5 Products" subtitle="Best performing items by revenue">
                        <Table
                            columns={productColumns}
                            data={topProductsList}
                            keyField="productId"
                            highlightTopRank
                            emptyMessage="No top product data recorded"
                        />
                    </Section>
                </div>

                {/* Inventory Alerts */}
                <div className="lg:col-span-5 min-w-0">
                    <Section title="Inventory Health Alerts" subtitle="Low stock & dead stock action items">
                        <AlertsSection threshold={5} days={30} />
                    </Section>
                </div>
            </div>
        </div>
    );
};

export default OverviewDashboard;
